import { supabase } from "@/integrations/supabase/client";

const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

export type SharePreview = {
  title: string;
  summary: string;
  niche: string | null;
  tags: string[];
  scores: { velocity: number; volume: number; relevance: number; total: number };
  sources: string[];
  signal_count: number;
  target_buyer: string | null;
  target_audience: string | null;
  product_concept: string | null;
  format: string | null;
  price_range: string | null;
  keyword_ideas: string[];
  first_steps: string[];
  locked: { detailed_blueprint: boolean; competitor_overlay: boolean; remaining_steps: number };
};

export type SharedBlueprint = {
  slug: string;
  headline: string | null;
  variant_key?: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image_url: string | null;
  expires_at: string | null;
  created_at: string;
  view_count: number;
  has_pdf: boolean;
  pdf_generated_at: string | null;
  description: string;
  preview: SharePreview;
};

export function slugify(title: string): string {
  const base = title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  const suffix = Math.random().toString(36).slice(2, 8);
  return `${base || "blueprint"}-${suffix}`;
}

export function shareUrl(slug: string): string {
  return `${window.location.origin}/b/${slug}`;
}

export function shareCardImageUrl(slug: string): string {
  return `${FUNCTIONS_BASE}/blueprint-share?slug=${encodeURIComponent(slug)}&og=1`;
}

/** Returns the existing share for a trend, or creates one. */
export async function getOrCreateShare(
  trendId: string,
  userId: string,
  title: string,
): Promise<{ slug: string; created: boolean }> {
  const { data: existing } = await supabase
    .from("blueprint_shares")
    .select("slug")
    .eq("trend_id", trendId)
    .eq("created_by", userId)
    .eq("is_active", true)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (existing?.slug) return { slug: existing.slug, created: false };

  const slug = slugify(title);
  const { error } = await supabase.from("blueprint_shares").insert({
    trend_id: trendId,
    created_by: userId,
    slug,
    headline: title.slice(0, 120),
  });
  if (error) throw new Error(error.message);
  return { slug, created: true };
}

export async function fetchSharedBlueprint(
  slug: string,
  countView = true,
  campaign: Record<string, string | null | undefined> = {},
): Promise<SharedBlueprint> {
  const tags = Object.entries(campaign)
    .filter(([, v]) => !!v)
    .map(([k, v]) => `&${k === "variant_key" ? "v" : k}=${encodeURIComponent(String(v))}`)
    .join("");
  const res = await fetch(
    `${FUNCTIONS_BASE}/blueprint-share?slug=${encodeURIComponent(slug)}${countView ? "&count=1" : ""}${tags}`,
    { headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string } },
  );
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? "Could not load this shared blueprint");
  return body as SharedBlueprint;
}

export type PdfResult = { url: string; cached: boolean; credits_spent: number; credits_left?: number };

/**
 * Asks the backend for the share's PDF. The first generation costs one
 * blueprint-download credit; afterwards anyone with the link downloads for free.
 */
export async function requestSharePdf(slug: string): Promise<PdfResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  const res = await fetch(`${FUNCTIONS_BASE}/blueprint-pdf`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ slug }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body?.error ?? "Could not prepare the PDF") as Error & { code?: string };
    err.code = body?.code;
    throw err;
  }
  return body as PdfResult;
}

/** Sets/replaces per-page social preview tags for a shared blueprint page. */
export function applyShareMeta(opts: { title: string; description: string; image: string; url: string }) {
  document.title = `${opts.title} — TrendsDiscovery blueprint`;
  const set = (selector: string, attrs: Record<string, string>) => {
    let el = document.head.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
    if (!el) {
      el = document.createElement(selector.startsWith("link") ? "link" : "meta");
      document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  };
  set('meta[name="description"]', { name: "description", content: opts.description.slice(0, 158) });
  set('meta[property="og:title"]', { property: "og:title", content: opts.title });
  set('meta[property="og:description"]', { property: "og:description", content: opts.description.slice(0, 200) });
  set('meta[property="og:type"]', { property: "og:type", content: "article" });
  set('meta[property="og:url"]', { property: "og:url", content: opts.url });
  set('meta[property="og:image"]', { property: "og:image", content: opts.image });
  set('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  set('meta[name="twitter:title"]', { name: "twitter:title", content: opts.title });
  set('meta[name="twitter:description"]', { name: "twitter:description", content: opts.description.slice(0, 200) });
  set('meta[name="twitter:image"]', { name: "twitter:image", content: opts.image });
  set('link[rel="canonical"]', { rel: "canonical", href: opts.url });
}


const SHARE_ATTRIBUTION_KEY = "td_share_slug";
const SHARE_CAMPAIGN_KEY = "td_share_campaign";

/** The campaign/variant tags the visitor arrived on, if any. */
export function storedShareCampaign(): Record<string, string | null> {
  try {
    return JSON.parse(localStorage.getItem(SHARE_CAMPAIGN_KEY) ?? "{}");
  } catch {
    return {};
  }
}

/** Remembers which share link (and campaign) brought a visitor in, for purchase attribution. */
export function storeShareAttribution(
  slug: string,
  campaign?: Record<string, string | null | undefined>,
) {
  try {
    localStorage.setItem(SHARE_ATTRIBUTION_KEY, slug);
    if (campaign) localStorage.setItem(SHARE_CAMPAIGN_KEY, JSON.stringify(campaign));
  } catch {
    /* storage unavailable — attribution is simply skipped */
  }
}

export function storedShareSlug(): string | null {
  try {
    return localStorage.getItem(SHARE_ATTRIBUTION_KEY);
  } catch {
    return null;
  }
}

/** Fire-and-forget public analytics event for a share link. */
export async function recordShareEvent(
  slug: string,
  eventType: "view" | "signup",
  campaign?: Record<string, string | null | undefined>,
) {
  try {
    const { data } = await supabase.auth.getSession();
    await fetch(`${FUNCTIONS_BASE}/blueprint-share-event`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
        ...(data.session?.access_token
          ? { Authorization: `Bearer ${data.session.access_token}` }
          : {}),
      },
      body: JSON.stringify({
        slug,
        event_type: eventType,
        referrer: document.referrer || null,
        ...(campaign ?? {}),
      }),
    });
  } catch {
    /* analytics must never break the page */
  }
}

export type ShareSettings = {
  og_title: string | null;
  og_description: string | null;
  og_image_url: string | null;
  expires_at: string | null;
  is_active: boolean;
};

export async function loadShareSettings(slug: string): Promise<ShareSettings | null> {
  const { data, error } = await supabase
    .from("blueprint_shares")
    .select("og_title,og_description,og_image_url,expires_at,is_active")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ShareSettings) ?? null;
}

/** Saves the creator's on-brand social preview overrides and link expiry. */
export async function saveShareSettings(slug: string, patch: Partial<ShareSettings>): Promise<void> {
  const clean: Partial<ShareSettings> = {};
  if (patch.og_title !== undefined) clean.og_title = patch.og_title?.trim().slice(0, 90) || null;
  if (patch.og_description !== undefined)
    clean.og_description = patch.og_description?.trim().slice(0, 200) || null;
  if (patch.og_image_url !== undefined) {
    const url = patch.og_image_url?.trim() || null;
    if (url && !/^https:\/\/\S+$/.test(url)) {
      throw new Error("The preview image must be a full https:// URL");
    }
    clean.og_image_url = url;
  }
  if (patch.expires_at !== undefined) clean.expires_at = patch.expires_at;
  if (patch.is_active !== undefined) clean.is_active = patch.is_active;
  const { error } = await supabase.from("blueprint_shares").update(clean).eq("slug", slug);
  if (error) throw new Error(error.message);
}

export type ShareEmailResult = {
  sent: string[];
  failed: { email: string; error: string }[];
  expires_at: string | null;
};

/** Emails the share link to up to five recipients, optionally with an expiry. */
export async function sendShareEmail(opts: {
  slug: string;
  recipients: string[];
  note?: string;
  expiresInDays?: number | null;
}): Promise<ShareEmailResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const res = await fetch(`${FUNCTIONS_BASE}/blueprint-share-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
      ...(sessionData.session?.access_token
        ? { Authorization: `Bearer ${sessionData.session.access_token}` }
        : {}),
    },
    body: JSON.stringify({
      slug: opts.slug,
      recipients: opts.recipients,
      note: opts.note ?? null,
      expires_in_days: opts.expiresInDays ?? null,
      base_url: window.location.origin,
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? "Could not send the blueprint email");
  return body as ShareEmailResult;
}

export type ShareAnalyticsRow = {
  share_id: string;
  slug: string;
  trend_id: string;
  trend_title: string;
  headline: string | null;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  views: number;
  unique_visitors: number;
  pdf_downloads: number;
  emails_sent: number;
  signups: number;
  purchases: number;
  revenue_cents: number;
  last_event_at: string | null;
};

export async function fetchShareAnalytics(): Promise<ShareAnalyticsRow[]> {
  const { data, error } = await supabase.rpc("share_analytics_for_user");
  if (error) throw new Error(error.message);
  return (data as ShareAnalyticsRow[]) ?? [];
}

export type ReferralAnalytics = {
  pending: number;
  completed: number;
  held_for_review: number;
  void: number;
  credits_earned: number;
  validated_revenue_cents: number;
};

export async function fetchReferralAnalytics(): Promise<ReferralAnalytics> {
  const { data, error } = await supabase.rpc("referral_analytics_for_user");
  if (error) throw new Error(error.message);
  return data as unknown as ReferralAnalytics;
}
